// src/components/AIAutomation.js
// Automatisation — decides whether an AI reply is sent automatically.
// Rule resolution (ai_automation_service): brand-specific rule wins → global
// rule → legacy automation toggle. The Règles tab renders that as a coverage
// list (one row per scope, decision spelled out: "envoi auto si confiance ≥
// 80 % · sinon brouillon / assigné à X"). Journal mirrors the router page:
// one toolbar, hero + approved/rejected composition bar, 6-col decisions
// table + drawer. Tabs live in ?tab=.
// The legacy auto-* CSS in AIAutomation.css stays — CreateAgent.js consumes
// it via global bundling; this page uses aa-* classes only.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FaPlus, FaPen, FaTrash, FaTimes, FaCheckCircle, FaTimesCircle, FaSync, FaDownload, FaExternalLinkAlt, FaSpinner, FaChevronDown, FaChevronRight, FaBolt, FaListAlt, FaLightbulb, FaCopy, FaSearch
} from 'react-icons/fa';
import './AIAutomation.css';

// The ticket workflow lives with the data sources now (/connectors: the
// journey + « Réglages de l'enrichissement », docs/ticket-journey.md).
const TABS = ['rules', 'journal', 'insights'];

const AIAutomation = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get('tab');
  const activeTab = TABS.includes(tabParam) ? tabParam : 'rules';
  useEffect(() => {
    if (tabParam === 'workflow') navigate('/connectors?view=workflow', { replace: true });
  }, [tabParam, navigate]);
  const setActiveTab = (tab) => {
    const next = new URLSearchParams(searchParams);
    if (tab === 'rules') next.delete('tab'); else next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  // ── Data ──
  const [rules, setRules] = useState(null); // null = loading
  const [brands, setBrands] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [stats, setStats] = useState(null);
  const [statsDays, setStatsDays] = useState(30);
  const [journalBrand, setJournalBrand] = useState('');
  const [logs, setLogs] = useState(null);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(1);
  const logsLimit = 20;
  const [logFilters, setLogFilters] = useState({ decision: '', scope: '', source: '', topic: '', assistant: '', q: '' });
  const [logSearch, setLogSearch] = useState('');
  const [facets, setFacets] = useState(null); // {sources, topics, assistants}
  const [selectedLog, setSelectedLog] = useState(null);
  const [showLogIds, setShowLogIds] = useState(false);

  // ── Insights (LLM analysis of rejected automations) ──
  const [insight, setInsight] = useState(undefined); // undefined = loading, null = none yet
  const [insightScope, setInsightScope] = useState('');
  const [insightGenerating, setInsightGenerating] = useState(false);
  const [insightError, setInsightError] = useState(null);
  const [copiedInstruction, setCopiedInstruction] = useState(null);

  // ── Modals ──
  // ruleModal = {editing, form, error, saving}
  const [ruleModal, setRuleModal] = useState(null);
  // deleteModal = {rule, deleting, error}
  const [deleteModal, setDeleteModal] = useState(null);

  // ── Feedback ──
  const [feedback, setFeedback] = useState(null);
  const feedbackTimer = useRef(null);
  const showFeedback = useCallback((type, message) => {
    clearTimeout(feedbackTimer.current);
    setFeedback({ type, message });
    if (type === 'success') {
      feedbackTimer.current = setTimeout(() => setFeedback(null), 5000);
    }
  }, []);
  useEffect(() => () => clearTimeout(feedbackTimer.current), []);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

  // ══════════ Fetchers ══════════

  const fetchRules = useCallback(async () => {
    try {
      const res = await axios.get('/api/ai-automation/rules', { headers: authHeaders() });
      setRules(res.data || []);
    } catch (err) {
      console.error('Error fetching rules:', err);
      setRules([]);
      showFeedback('error', t('aiAutomation.fetchError', 'Impossible de charger les règles.'));
    }
  }, [showFeedback, t]);

  const appendLogFilterParams = useCallback((params) => {
    if (journalBrand) params.append('brand_id', journalBrand);
    if (logFilters.scope) params.append('scope', logFilters.scope);
    if (logFilters.source) params.append('source', logFilters.source);
    if (logFilters.topic) params.append('topic', logFilters.topic);
    if (logFilters.assistant) params.append('assistant_id', logFilters.assistant);
    if (logFilters.q) params.append('q', logFilters.q);
  }, [journalBrand, logFilters]);

  const fetchStats = useCallback(async () => {
    try {
      const params = new URLSearchParams({ days: statsDays });
      appendLogFilterParams(params);
      const res = await axios.get(`/api/ai-automation/logs/stats?${params.toString()}`, { headers: authHeaders() });
      setStats(res.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
      setStats(null);
    }
  }, [statsDays, appendLogFilterParams]);

  const fetchLogs = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: logsPage, limit: logsLimit, days: statsDays });
      appendLogFilterParams(params);
      if (logFilters.decision) params.append('decision', logFilters.decision);
      const res = await axios.get(`/api/ai-automation/logs?${params.toString()}`, { headers: authHeaders() });
      setLogs(res.data.logs || []);
      setLogsTotal(res.data.total || 0);
    } catch (err) {
      console.error('Error fetching logs:', err);
      setLogs([]);
    }
  }, [logsPage, statsDays, appendLogFilterParams, logFilters.decision]);

  useEffect(() => {
    const headers = authHeaders();
    axios.get('/api/brands/list', { headers })
      .then((res) => setBrands(res.data || []))
      .catch((err) => console.error('Error fetching brands:', err));
    axios.get('/api/employees/agents/basic', { headers })
      .then((res) => setEmployees(res.data || []))
      .catch((err) => console.error('Error fetching employees:', err));
    fetchRules();
  }, [fetchRules]);

  useEffect(() => {
    if (activeTab === 'journal') {
      fetchLogs();
      fetchStats();
    }
  }, [activeTab, fetchLogs, fetchStats]);

  // Dropdown facets (sources / topics / AI agents), fetched once.
  useEffect(() => {
    if ((activeTab === 'journal' || activeTab === 'insights') && facets === null) {
      axios.get('/api/ai-automation/logs/facets', { headers: authHeaders() })
        .then((res) => setFacets(res.data))
        .catch((err) => {
          console.error('Error fetching log facets:', err);
          setFacets({ sources: [], topics: [], assistants: [] });
        });
    }
  }, [activeTab, facets]);

  // Debounced free-text search.
  useEffect(() => {
    const q = logSearch.trim();
    if (q === logFilters.q) return undefined;
    const timer = setTimeout(() => {
      setLogFilters((f) => ({ ...f, q }));
      setLogsPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [logSearch, logFilters.q]);

  // Latest stored insight for the current parameters.
  useEffect(() => {
    if (activeTab !== 'insights') return;
    setInsight(undefined);
    setInsightError(null);
    const params = new URLSearchParams({ days: statsDays });
    if (journalBrand) params.append('brand_id', journalBrand);
    if (insightScope) params.append('scope', insightScope);
    axios.get(`/api/ai-automation/insights?${params.toString()}`, { headers: authHeaders() })
      .then((res) => setInsight(res.data.insight))
      .catch((err) => {
        console.error('Error fetching insights:', err);
        setInsight(null);
      });
  }, [activeTab, statsDays, journalBrand, insightScope]);

  const generateInsight = async () => {
    setInsightGenerating(true);
    setInsightError(null);
    try {
      const res = await axios.post('/api/ai-automation/insights/generate', {
        days: Number(statsDays),
        brand_id: journalBrand || null,
        scope: insightScope || null,
        language: (i18n.language || 'fr').split('-')[0],
      }, { headers: authHeaders() });
      setInsight(res.data.insight);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string' && detail.startsWith('not_enough_rejections:')) {
        const [, found, min] = detail.split(':');
        setInsightError(t('aiAutomation.notEnoughRejections',
          'Pas assez de refus à analyser sur cette période ({{found}} trouvé(s), minimum {{min}}). Élargissez la période ou les filtres.',
          { found, min }));
      } else {
        setInsightError(t('aiAutomation.insightsError', 'Échec de la génération de l’analyse. Réessayez.'));
      }
    } finally {
      setInsightGenerating(false);
    }
  };

  const copyInstruction = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedInstruction(key);
      setTimeout(() => setCopiedInstruction(null), 2500);
    } catch (err) {
      console.error('Clipboard copy failed:', err);
    }
  };

  // ══════════ Helpers ══════════

  const brandId = (b) => b.id || b._id;
  const getBrandName = (id) => {
    if (!id) return t('aiAutomation.allBrands', 'Toutes les marques');
    const brand = brands.find((b) => String(brandId(b)) === String(id));
    return brand?.name || id;
  };

  const employeeName = (id) => {
    const emp = employees.find((e) => String(e.id || e._id) === String(id));
    return emp?.name || emp?.email || id;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString(i18n.language || 'fr', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  const SCOPE_LABELS = {
    all: t('aiAutomation.scopeAll', 'Tous les tickets'),
    client: t('aiAutomation.scopeClient', 'Client'),
    carrier: t('aiAutomation.scopeCarrier', 'Transporteur'),
  };

  const sourceLabel = (s) => (s === 'web_form' ? t('aiAutomation.webForm', 'Formulaire web') : (s || '—'));

  const SEVERITY_LABELS = {
    high: t('aiAutomation.severityHigh', 'Impact fort'),
    medium: t('aiAutomation.severityMedium', 'Impact moyen'),
    low: t('aiAutomation.severityLow', 'Impact faible'),
  };

  const ruleSentence = (rule) => {
    const threshold = t('aiAutomation.ruleThreshold', 'envoi auto si confiance ≥ {{pct}} %', {
      pct: Math.round((rule.confidence_threshold || 0) * 100),
    });
    const fallback = rule.fallback_action === 'assign_employee'
      ? t('aiAutomation.ruleFallbackAssign', 'sinon assigné à {{name}}', {
          name: rule.fallback_employee_id ? employeeName(rule.fallback_employee_id) : '—',
        })
      : t('aiAutomation.ruleFallbackDraft', 'sinon brouillon à valider');
    return `${threshold} · ${fallback}`;
  };

  // ══════════ Rules CRUD ══════════

  const openRuleModal = (rule = null, presetBrandId = '') => {
    setRuleModal({
      editing: rule,
      form: rule ? {
        brand_id: rule.brand_id || '',
        is_active: rule.is_active,
        scope: rule.scope,
        instructions: rule.instructions,
        confidence_threshold: rule.confidence_threshold,
        fallback_action: rule.fallback_action,
        fallback_employee_id: rule.fallback_employee_id || '',
      } : {
        brand_id: presetBrandId,
        is_active: true,
        scope: 'all',
        instructions: '',
        confidence_threshold: 0.8,
        fallback_action: 'draft',
        fallback_employee_id: '',
      },
      error: null,
      saving: false,
    });
  };

  const setRuleForm = (patch) => setRuleModal((m) => ({ ...m, form: { ...m.form, ...patch }, error: null }));

  const saveRule = async () => {
    const { form, editing } = ruleModal;
    if (!form.instructions.trim()) {
      setRuleModal((m) => ({ ...m, error: t('aiAutomation.instructionsRequired', 'Les instructions sont requises.') }));
      return;
    }
    if (form.fallback_action === 'assign_employee' && !form.fallback_employee_id) {
      setRuleModal((m) => ({ ...m, error: t('aiAutomation.employeeRequired', 'Choisissez l’employé de repli.') }));
      return;
    }
    setRuleModal((m) => ({ ...m, saving: true, error: null }));
    try {
      const payload = {
        is_active: form.is_active,
        scope: form.scope,
        instructions: form.instructions,
        confidence_threshold: form.confidence_threshold,
        fallback_action: form.fallback_action,
        fallback_employee_id: form.fallback_employee_id || null,
      };
      if (editing) {
        await axios.put(`/api/ai-automation/rules/${editing.id}`, payload, { headers: authHeaders() });
        showFeedback('success', t('aiAutomation.ruleUpdated', 'Règle mise à jour.'));
      } else {
        await axios.post('/api/ai-automation/rules', { ...payload, brand_id: form.brand_id || null }, { headers: authHeaders() });
        showFeedback('success', t('aiAutomation.ruleCreated', 'Règle créée.'));
      }
      setRuleModal(null);
      fetchRules();
    } catch (err) {
      setRuleModal((m) => ({
        ...m, saving: false,
        error: err.response?.data?.detail || t('aiAutomation.saveError', 'Échec de l’enregistrement de la règle.'),
      }));
    }
  };

  const confirmDeleteRule = async () => {
    setDeleteModal((m) => ({ ...m, deleting: true, error: null }));
    try {
      await axios.delete(`/api/ai-automation/rules/${deleteModal.rule.id}`, { headers: authHeaders() });
      setDeleteModal(null);
      showFeedback('success', t('aiAutomation.ruleDeleted', 'Règle supprimée.'));
      fetchRules();
    } catch (err) {
      setDeleteModal((m) => ({
        ...m, deleting: false,
        error: err.response?.data?.detail || t('aiAutomation.deleteError', 'Échec de la suppression de la règle.'),
      }));
    }
  };

  const toggleRule = async (rule) => {
    try {
      await axios.put(`/api/ai-automation/rules/${rule.id}`, { is_active: !rule.is_active }, { headers: authHeaders() });
      fetchRules();
    } catch (err) {
      showFeedback('error', t('aiAutomation.toggleError', 'Échec de l’activation/désactivation.'));
    }
  };

  // ══════════ CSV export ══════════

  const handleExportCSV = async () => {
    try {
      const params = new URLSearchParams({ days: statsDays });
      appendLogFilterParams(params);
      if (logFilters.decision) params.append('decision', logFilters.decision);
      const res = await axios.get(`/api/ai-automation/logs/export?${params.toString()}`, {
        headers: authHeaders(), responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'automation_logs.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting logs:', err);
      showFeedback('error', t('aiAutomation.exportError', 'Échec de l’export CSV.'));
    }
  };

  // ══════════ Derived ══════════

  const periodLabel = {
    7: t('aiAutomation.last7', '7 derniers jours'),
    30: t('aiAutomation.last30', '30 derniers jours'),
    90: t('aiAutomation.last90', '90 derniers jours'),
    365: t('aiAutomation.lastYear', '12 derniers mois'),
  }[statsDays];

  const totalLogsPages = Math.max(1, Math.ceil(logsTotal / logsLimit));
  const getPageItems = () => {
    const anchors = new Set([1, totalLogsPages, logsPage - 1, logsPage, logsPage + 1]);
    const pages = [...anchors].filter((p) => p >= 1 && p <= totalLogsPages).sort((a, b) => a - b);
    const items = [];
    let prev = 0;
    pages.forEach((p) => {
      if (p - prev > 1) items.push(`gap-${p}`);
      items.push(p);
      prev = p;
    });
    return items;
  };

  const skeleton = (rows = 4) => (
    <div className="aa-skeleton" aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => <div key={i} className="aa-skeleton-row" />)}
    </div>
  );

  // ══════════ Render: coverage row ══════════

  const renderScopeRow = (scopeBrandId, scopeName) => {
    const own = (rules || []).filter((r) => String(r.brand_id || '') === String(scopeBrandId || ''));
    const isGlobalScope = !scopeBrandId;
    const globalRules = (rules || []).filter((r) => !r.brand_id && r.is_active);

    return (
      <li key={scopeBrandId || 'global'} className="aa-cov-row">
        <div className="aa-cov-scope">
          <span className="aa-cov-name">{scopeName}</span>
          {isGlobalScope && (
            <span className="aa-cov-fallback">{t('aiAutomation.covFallback', 'portée par défaut')}</span>
          )}
        </div>
        <div className="aa-cov-body">
          {own.length === 0 ? (
            <div className="aa-cov-line">
              <span className="aa-cov-state none">
                {!isGlobalScope && globalRules.length > 0
                  ? t('aiAutomation.covCoveredByGlobal', 'Couvert par « Toutes les marques »')
                  : t('aiAutomation.covNoRule', 'Aucune règle — bascule d’automatisation héritée')}
              </span>
              <button type="button" className="aa-cov-add" onClick={() => openRuleModal(null, scopeBrandId || '')}>
                <FaPlus /> {t('aiAutomation.covAdd', 'Ajouter')}
              </button>
            </div>
          ) : own.map((rule) => (
            <div key={rule.id} className="aa-cov-line">
              <span className={`aa-scope-chip ${rule.scope}`}>{SCOPE_LABELS[rule.scope] || rule.scope}</span>
              <span className={`aa-cov-sentence ${rule.is_active ? '' : 'inactive'}`} title={rule.instructions}>
                {rule.is_active
                  ? ruleSentence(rule)
                  : t('aiAutomation.ruleInactive', 'Désactivée — aucune réponse automatique')}
              </span>
              <div className="aa-cov-actions">
                <button
                  type="button"
                  role="switch"
                  aria-checked={rule.is_active}
                  className={`aa-switch ${rule.is_active ? 'on' : ''}`}
                  onClick={() => toggleRule(rule)}
                  title={rule.is_active ? t('aiAutomation.deactivate', 'Désactiver') : t('aiAutomation.activate', 'Activer')}
                >
                  <span className="aa-switch-knob" />
                </button>
                <button type="button" className="aa-icon-btn" onClick={() => openRuleModal(rule)} title={t('aiAutomation.edit', 'Modifier')} aria-label={t('aiAutomation.edit', 'Modifier')}>
                  <FaPen />
                </button>
                <button
                  type="button"
                  className="aa-icon-btn danger"
                  onClick={() => setDeleteModal({ rule, deleting: false, error: null })}
                  title={t('aiAutomation.delete', 'Supprimer')}
                  aria-label={t('aiAutomation.delete', 'Supprimer')}
                >
                  <FaTrash />
                </button>
              </div>
            </div>
          ))}
        </div>
      </li>
    );
  };

  // ══════════ Render: tabs ══════════

  const renderRulesTab = () => (
    <div className="aa-card">
      <div className="aa-card-header">
        <div className="aa-card-heading">
          <h2>{t('aiAutomation.rulesTitle', 'Règles & couverture')}</h2>
          <p>{t('aiAutomation.rulesSubtitle', 'Une réponse IA n’est envoyée automatiquement que si sa confiance atteint le seuil de la règle applicable — règle de marque d’abord, sinon règle globale, sinon la bascule héritée.')}</p>
        </div>
        <button type="button" className="u-btn u-btn--primary" onClick={() => openRuleModal()}>
          <FaPlus /> {t('aiAutomation.createRule', 'Créer une règle')}
        </button>
      </div>
      {rules === null ? skeleton(3) : (
        <ul className="aa-cov-list">
          {renderScopeRow(null, t('aiAutomation.allBrands', 'Toutes les marques'))}
          {brands.map((b) => renderScopeRow(brandId(b), b.name))}
        </ul>
      )}
    </div>
  );

  const renderJournalTab = () => {
    const total = stats?.total_evaluations || 0;
    const approved = stats?.approved_count || 0;
    const rejected = stats?.rejected_count || 0;
    return (
      <div className="aa-stack">
        {/* Toolbar */}
        <div className="aa-toolbar">
          <label className="aa-toolbar-field">
            <span>{t('aiAutomation.period', 'Période')}</span>
            <select value={statsDays} onChange={(e) => { setStatsDays(Number(e.target.value)); setLogsPage(1); }}>
              <option value={7}>{t('aiAutomation.last7', '7 derniers jours')}</option>
              <option value={30}>{t('aiAutomation.last30', '30 derniers jours')}</option>
              <option value={90}>{t('aiAutomation.last90', '90 derniers jours')}</option>
              <option value={365}>{t('aiAutomation.lastYear', '12 derniers mois')}</option>
            </select>
          </label>
          {brands.length > 0 && (
            <label className="aa-toolbar-field">
              <span>{t('aiAutomation.brand', 'Marque')}</span>
              <select value={journalBrand} onChange={(e) => { setJournalBrand(e.target.value); setLogsPage(1); }}>
                <option value="">{t('aiAutomation.allBrands', 'Toutes les marques')}</option>
                {brands.map((b) => <option key={brandId(b)} value={brandId(b)}>{b.name}</option>)}
              </select>
            </label>
          )}
          <label className="aa-toolbar-field">
            <span>{t('aiAutomation.decision', 'Décision')}</span>
            <select value={logFilters.decision} onChange={(e) => { setLogFilters((f) => ({ ...f, decision: e.target.value })); setLogsPage(1); }}>
              <option value="">{t('aiAutomation.all', 'Toutes')}</option>
              <option value="approved">{t('aiAutomation.approved', 'Approuvée')}</option>
              <option value="rejected">{t('aiAutomation.rejected', 'Rejetée')}</option>
            </select>
          </label>
          <label className="aa-toolbar-field">
            <span>{t('aiAutomation.scope', 'Portée')}</span>
            <select value={logFilters.scope} onChange={(e) => { setLogFilters((f) => ({ ...f, scope: e.target.value })); setLogsPage(1); }}>
              <option value="">{t('aiAutomation.all', 'Toutes')}</option>
              <option value="client">{SCOPE_LABELS.client}</option>
              <option value="carrier">{SCOPE_LABELS.carrier}</option>
            </select>
          </label>
          <label className="aa-toolbar-field">
            <span>{t('aiAutomation.source', 'Source')}</span>
            <select value={logFilters.source} onChange={(e) => { setLogFilters((f) => ({ ...f, source: e.target.value })); setLogsPage(1); }}>
              <option value="">{t('aiAutomation.all', 'Toutes')}</option>
              {(facets?.sources || []).map((s) => <option key={s} value={s}>{sourceLabel(s)}</option>)}
              {facets !== null && logFilters.source && !(facets.sources || []).includes(logFilters.source) && (
                <option value={logFilters.source}>{sourceLabel(logFilters.source)}</option>
              )}
            </select>
          </label>
          {(facets?.topics || []).length > 0 && (
            <label className="aa-toolbar-field">
              <span>{t('aiAutomation.ticketTopic', 'Sujet')}</span>
              <select value={logFilters.topic} onChange={(e) => { setLogFilters((f) => ({ ...f, topic: e.target.value })); setLogsPage(1); }}>
                <option value="">{t('aiAutomation.all', 'Toutes')}</option>
                {facets.topics.map((tp) => <option key={tp} value={tp}>{tp}</option>)}
              </select>
            </label>
          )}
          {(facets?.assistants || []).length > 0 && (
            <label className="aa-toolbar-field">
              <span>{t('aiAutomation.aiAgent', 'Agent IA')}</span>
              <select value={logFilters.assistant} onChange={(e) => { setLogFilters((f) => ({ ...f, assistant: e.target.value })); setLogsPage(1); }}>
                <option value="">{t('aiAutomation.all', 'Toutes')}</option>
                {facets.assistants.map((a) => <option key={a.assistant_id} value={a.assistant_id}>{a.name}</option>)}
              </select>
            </label>
          )}
          <label className="aa-toolbar-field aa-toolbar-search">
            <span>{t('aiAutomation.search', 'Recherche')}</span>
            <div className="aa-search-box">
              <FaSearch aria-hidden="true" />
              <input
                type="search"
                value={logSearch}
                onChange={(e) => setLogSearch(e.target.value)}
                placeholder={t('aiAutomation.searchPlaceholder', 'Objet, n° de suivi, raison…')}
              />
            </div>
          </label>
          <div className="aa-toolbar-actions">
            <button type="button" className="aa-icon-btn" onClick={() => { fetchStats(); fetchLogs(); }} title={t('aiAutomation.refresh', 'Actualiser')} aria-label={t('aiAutomation.refresh', 'Actualiser')}>
              <FaSync />
            </button>
            <button type="button" className="u-btn u-btn--secondary" onClick={handleExportCSV}>
              <FaDownload /> {t('aiAutomation.exportCSV', 'Export CSV')}
            </button>
          </div>
        </div>

        {/* Stats */}
        {stats && total > 0 && (
          <div className="aa-card aa-stats-card">
            <div className="aa-stats-hero">
              <div className="aa-stats-hero-main">
                <span className="aa-stats-hero-value">{total.toLocaleString(i18n.language || 'fr')}</span>
                <span className="aa-stats-hero-label">
                  {t('aiAutomation.totalEvals', 'évaluations')} · {periodLabel}
                  {journalBrand && ` · ${getBrandName(journalBrand)}`}
                </span>
              </div>
              <div className="aa-stats-composition">
                <div className="aa-comp-bar" role="img" aria-label={t('aiAutomation.compositionAria', 'Répartition approuvées / rejetées')}>
                  <span className="aa-comp-seg approved" style={{ width: `${(approved / total) * 100}%` }} />
                  <span className="aa-comp-seg rejected" style={{ width: `${(rejected / total) * 100}%` }} />
                </div>
                <div className="aa-comp-legend">
                  <span className="aa-comp-item approved">
                    {t('aiAutomation.approvedCount', '{{total}} approuvées ({{rate}} %)', {
                      total: approved.toLocaleString(i18n.language || 'fr'),
                      rate: ((stats.approval_rate || 0) * 100).toFixed(1),
                    })}
                  </span>
                  <span className="aa-comp-item rejected">
                    {t('aiAutomation.rejectedCount', '{{total}} rejetées', { total: rejected.toLocaleString(i18n.language || 'fr') })}
                  </span>
                </div>
              </div>
              <div className="aa-stats-side">
                {typeof stats.avg_confidence === 'number' && (
                  <div className="aa-stats-fact">
                    <span className="aa-stats-fact-value">{(stats.avg_confidence * 100).toFixed(1)} %</span>
                    <span className="aa-stats-fact-label">{t('aiAutomation.avgConfidence', 'confiance moyenne')}</span>
                  </div>
                )}
                {typeof stats.avg_processing_time_ms === 'number' && stats.avg_processing_time_ms > 0 && (
                  <div className="aa-stats-fact">
                    <span className="aa-stats-fact-value">{Math.round(stats.avg_processing_time_ms).toLocaleString(i18n.language || 'fr')} ms</span>
                    <span className="aa-stats-fact-label">{t('aiAutomation.avgProcessing', 'traitement moyen')}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Decisions table */}
        <div className="aa-card">
          <div className="aa-card-header">
            <div className="aa-card-heading">
              <h2>
                {t('aiAutomation.logsTitle', 'Décisions')}
                {logs !== null && <span className="aa-card-count">{logsTotal.toLocaleString(i18n.language || 'fr')}</span>}
              </h2>
            </div>
          </div>
          {logs === null ? skeleton(6) : logs.length === 0 ? (
            <div className="aa-empty">
              <p>{t('aiAutomation.noLogs', 'Aucune décision d’automatisation ne correspond aux filtres.')}</p>
            </div>
          ) : (
            <>
              <div className="aa-table-scroll">
                <table className="aa-table">
                  <colgroup>
                    <col style={{ width: '150px' }} />
                    <col style={{ width: '260px' }} />
                    <col style={{ width: '150px' }} />
                    <col style={{ width: '120px' }} />
                    <col style={{ width: '110px' }} />
                    <col style={{ width: '125px' }} />
                    <col style={{ width: '100px' }} />
                    <col style={{ width: '85px' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>{t('aiAutomation.date', 'Date')}</th>
                      <th>{t('aiAutomation.ticketCol', 'Ticket')}</th>
                      <th>{t('aiAutomation.aiAgent', 'Agent IA')}</th>
                      <th>{t('aiAutomation.source', 'Source')}</th>
                      <th>{t('aiAutomation.scope', 'Portée')}</th>
                      <th>{t('aiAutomation.decision', 'Décision')}</th>
                      <th className="aa-th-num">{t('aiAutomation.confidence', 'Confiance')}</th>
                      <th className="aa-th-num">{t('aiAutomation.processing', 'Temps')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => (
                      <tr
                        key={log.id}
                        className={`aa-log-row ${selectedLog?.id === log.id ? 'selected' : ''}`}
                        tabIndex={0}
                        onClick={() => { setSelectedLog(log); setShowLogIds(false); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { setSelectedLog(log); setShowLogIds(false); } }}
                      >
                        <td>{formatDate(log.created_at)}</td>
                        <td className="aa-td-ticket">
                          <span className="aa-ticket-cell">
                            {log.ticket_topic && <span className="aa-topic-chip">{log.ticket_topic}</span>}
                            <span className="aa-ticket-subject" title={log.ticket_subject || undefined}>
                              {log.ticket_subject || '—'}
                            </span>
                          </span>
                        </td>
                        <td title={log.assistant_name || undefined}>{log.assistant_name || '—'}</td>
                        <td>{sourceLabel(log.source)}</td>
                        <td>{SCOPE_LABELS[log.scope] || log.scope || '—'}</td>
                        <td>
                          <span className={`aa-decision ${log.decision}`}>
                            <span className="aa-decision-dot" aria-hidden="true" />
                            {log.decision === 'approved'
                              ? t('aiAutomation.approved', 'Approuvée')
                              : t('aiAutomation.rejected', 'Rejetée')}
                          </span>
                        </td>
                        <td className="aa-td-num">{typeof log.confidence === 'number' ? `${(log.confidence * 100).toFixed(0)} %` : '—'}</td>
                        <td className="aa-td-num">{typeof log.processing_time_ms === 'number' ? `${log.processing_time_ms} ms` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {totalLogsPages > 1 && (
                <div className="aa-card-footer">
                  <nav className="aa-pager" aria-label={t('aiAutomation.pagination', 'Pagination')}>
                    <button type="button" disabled={logsPage === 1} onClick={() => setLogsPage((p) => p - 1)} aria-label={t('common.previous')}>‹</button>
                    {getPageItems().map((item) => (
                      typeof item === 'number' ? (
                        <button
                          key={item}
                          type="button"
                          className={item === logsPage ? 'active' : ''}
                          aria-current={item === logsPage ? 'page' : undefined}
                          onClick={() => setLogsPage(item)}
                        >
                          {item}
                        </button>
                      ) : (
                        <span key={item} className="aa-pager-ellipsis" aria-hidden="true">…</span>
                      )
                    ))}
                    <button type="button" disabled={logsPage === totalLogsPages} onClick={() => setLogsPage((p) => p + 1)} aria-label={t('common.next')}>›</button>
                  </nav>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  const renderInsightsTab = () => (
    <div className="aa-stack">
      {/* Toolbar */}
      <div className="aa-toolbar">
        <label className="aa-toolbar-field">
          <span>{t('aiAutomation.period', 'Période')}</span>
          <select value={statsDays} onChange={(e) => setStatsDays(Number(e.target.value))}>
            <option value={7}>{t('aiAutomation.last7', '7 derniers jours')}</option>
            <option value={30}>{t('aiAutomation.last30', '30 derniers jours')}</option>
            <option value={90}>{t('aiAutomation.last90', '90 derniers jours')}</option>
            <option value={365}>{t('aiAutomation.lastYear', '12 derniers mois')}</option>
          </select>
        </label>
        {brands.length > 0 && (
          <label className="aa-toolbar-field">
            <span>{t('aiAutomation.brand', 'Marque')}</span>
            <select value={journalBrand} onChange={(e) => setJournalBrand(e.target.value)}>
              <option value="">{t('aiAutomation.allBrands', 'Toutes les marques')}</option>
              {brands.map((b) => <option key={brandId(b)} value={brandId(b)}>{b.name}</option>)}
            </select>
          </label>
        )}
        <label className="aa-toolbar-field">
          <span>{t('aiAutomation.scope', 'Portée')}</span>
          <select value={insightScope} onChange={(e) => setInsightScope(e.target.value)}>
            <option value="">{t('aiAutomation.all', 'Toutes')}</option>
            <option value="client">{SCOPE_LABELS.client}</option>
            <option value="carrier">{SCOPE_LABELS.carrier}</option>
          </select>
        </label>
        <div className="aa-toolbar-actions">
          <button type="button" className="u-btn u-btn--primary" onClick={generateInsight} disabled={insightGenerating || insight === undefined}>
            {insightGenerating
              ? (<><FaSpinner className="aa-spin" /> {t('aiAutomation.insightsGenerating', 'Analyse en cours…')}</>)
              : (<><FaLightbulb /> {insight
                  ? t('aiAutomation.insightsRegenerate', 'Régénérer l’analyse')
                  : t('aiAutomation.insightsGenerate', 'Analyser les refus')}</>)}
          </button>
        </div>
      </div>

      {insightError && (
        <div className="aa-feedback error" role="status"><FaTimesCircle /> {insightError}</div>
      )}

      {insight === undefined ? skeleton(4) : !insight ? (
        <div className="aa-card">
          <div className="aa-empty aa-insight-empty">
            <FaLightbulb aria-hidden="true" />
            <p><b>{t('aiAutomation.insightsEmptyTitle', 'Aucune analyse pour ces critères')}</b></p>
            <p>{t('aiAutomation.insightsEmptyBody', 'Lancez une analyse IA des refus d’automatisation : elle identifie ce qui bloque vos réponses automatiques, pourquoi, et ce qu’il faut améliorer (règles, instructions d’agents, configuration).')}</p>
          </div>
        </div>
      ) : (
        <>
          {/* Summary */}
          <div className="aa-card aa-insight-card">
            <div className="aa-card-header">
              <div className="aa-card-heading">
                <h2>{t('aiAutomation.insightsSummaryTitle', 'Synthèse')}</h2>
                <p className="aa-insight-meta">
                  {t('aiAutomation.insightsMeta', 'Générée le {{date}} · {{analyzed}} refus analysés sur {{rejected}} · {{evals}} évaluations sur la période', {
                    date: formatDate(insight.generated_at),
                    analyzed: insight.logs_analyzed,
                    rejected: insight.rejected_total,
                    evals: insight.total_evaluations,
                  })}
                </p>
              </div>
            </div>
            <p className="aa-insight-summary">{insight.summary}</p>
          </div>

          {/* Patterns */}
          {insight.patterns.length > 0 && (
            <div className="aa-card">
              <div className="aa-card-header">
                <div className="aa-card-heading">
                  <h2>
                    {t('aiAutomation.insightsPatternsTitle', 'Schémas de refus')}
                    <span className="aa-card-count">{insight.patterns.length}</span>
                  </h2>
                </div>
              </div>
              <ul className="aa-pattern-list">
                {insight.patterns.map((p, idx) => (
                  <li key={idx} className="aa-pattern">
                    <div className="aa-pattern-head">
                      <span className={`aa-severity ${p.severity}`}>{SEVERITY_LABELS[p.severity] || p.severity}</span>
                      <h3>{p.title}</h3>
                      {p.estimated_share_pct > 0 && (
                        <span className="aa-pattern-share">
                          {t('aiAutomation.patternShare', '≈ {{pct}} % des refus', { pct: Math.round(p.estimated_share_pct) })}
                        </span>
                      )}
                    </div>
                    <p className="aa-pattern-desc">{p.description}</p>
                    <div className="aa-pattern-detail">
                      <span className="aa-pattern-label">{t('aiAutomation.rootCause', 'Pourquoi')}</span>
                      <p>{p.root_cause}</p>
                    </div>
                    <div className="aa-pattern-detail">
                      <span className="aa-pattern-label">{t('aiAutomation.recommendationLabel', 'Recommandation')}</span>
                      <p>{p.recommendation}</p>
                    </div>
                    {p.suggested_instruction && (
                      <div className="aa-pattern-instruction">
                        <div className="aa-pattern-instruction-head">
                          <span className="aa-pattern-label">{t('aiAutomation.suggestedInstruction', 'Instruction prête à coller dans vos règles')}</span>
                          <button type="button" className="aa-copy-btn" onClick={() => copyInstruction(p.suggested_instruction, idx)}>
                            <FaCopy /> {copiedInstruction === idx ? t('aiAutomation.copied', 'Copié !') : t('aiAutomation.copy', 'Copier')}
                          </button>
                        </div>
                        <pre>{p.suggested_instruction}</pre>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Gaps + working well */}
          {(insight.rule_gaps.length > 0 || insight.working_well.length > 0) && (
            <div className="aa-insight-columns">
              {insight.rule_gaps.length > 0 && (
                <div className="aa-card">
                  <div className="aa-card-header">
                    <div className="aa-card-heading">
                      <h2>{t('aiAutomation.ruleGapsTitle', 'Non couvert par vos règles')}</h2>
                    </div>
                  </div>
                  <ul className="aa-insight-list gaps">
                    {insight.rule_gaps.map((g, idx) => <li key={idx}>{g}</li>)}
                  </ul>
                </div>
              )}
              {insight.working_well.length > 0 && (
                <div className="aa-card">
                  <div className="aa-card-header">
                    <div className="aa-card-heading">
                      <h2>{t('aiAutomation.workingWellTitle', 'Ce qui fonctionne bien')}</h2>
                    </div>
                  </div>
                  <ul className="aa-insight-list well">
                    {insight.working_well.map((w, idx) => <li key={idx}>{w}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );

  // ══════════ Render ══════════

  return (
    <div className="ai-automation-page">
      <div className="aa-header">
        <h1>{t('aiAutomation.title', 'Automatisation')}</h1>
        <p className="aa-header-subtitle">
          {t('aiAutomation.subtitle', 'Décide quelles réponses de l’IA sont envoyées automatiquement, et ce qu’il advient des autres.')}
        </p>
      </div>

      <div className="aa-tabs" role="tablist" aria-label={t('aiAutomation.title', 'Automatisation')}>
        <button type="button" role="tab" aria-selected={activeTab === 'rules'} className={`aa-tab ${activeTab === 'rules' ? 'active' : ''}`} onClick={() => setActiveTab('rules')}>
          <FaBolt aria-hidden="true" /> {t('aiAutomation.rulesTab', 'Règles')}
        </button>
        <button type="button" role="tab" aria-selected={activeTab === 'journal'} className={`aa-tab ${activeTab === 'journal' ? 'active' : ''}`} onClick={() => setActiveTab('journal')}>
          <FaListAlt aria-hidden="true" /> {t('aiAutomation.journalTab', 'Journal')}
        </button>
        <button type="button" role="tab" aria-selected={activeTab === 'insights'} className={`aa-tab ${activeTab === 'insights' ? 'active' : ''}`} onClick={() => setActiveTab('insights')}>
          <FaLightbulb aria-hidden="true" /> {t('aiAutomation.insightsTab', 'Recommandations')}
        </button>
      </div>

      {feedback && (
        <div className={`aa-feedback ${feedback.type}`} role="status">
          {feedback.type === 'success' ? <FaCheckCircle /> : <FaTimesCircle />}
          {feedback.message}
        </div>
      )}

      {activeTab === 'rules' && renderRulesTab()}
      {activeTab === 'journal' && renderJournalTab()}
      {activeTab === 'insights' && renderInsightsTab()}

      {/* Log detail drawer */}
      {selectedLog && (
        <>
          <div className="aa-drawer-backdrop" onClick={() => setSelectedLog(null)} />
          <aside className="aa-drawer" role="dialog" aria-modal="true">
            <div className="aa-drawer-header">
              <h3>{t('aiAutomation.logDetail', 'Décision d’automatisation')}</h3>
              <button type="button" className="aa-icon-btn" onClick={() => setSelectedLog(null)} aria-label={t('common.cancel', 'Fermer')}>
                <FaTimes />
              </button>
            </div>
            <div className="aa-drawer-body">
              <div className="aa-drawer-decision">
                <span className={`aa-decision large ${selectedLog.decision}`}>
                  <span className="aa-decision-dot" aria-hidden="true" />
                  {selectedLog.decision === 'approved'
                    ? t('aiAutomation.approvedLong', 'Approuvée — réponse envoyée automatiquement')
                    : t('aiAutomation.rejectedLong', 'Rejetée — repli appliqué')}
                </span>
                {typeof selectedLog.confidence === 'number' && (
                  <span className="aa-drawer-confidence">
                    {t('aiAutomation.confidenceValue', 'confiance {{pct}} %', { pct: (selectedLog.confidence * 100).toFixed(0) })}
                  </span>
                )}
              </div>

              <div className="aa-drawer-meta">
                {selectedLog.ticket_subject && (
                  <div className="aa-drawer-meta-wide">
                    <span className="aa-drawer-meta-label">{t('aiAutomation.ticketSubject', 'Objet du ticket')}</span>
                    <span>{selectedLog.ticket_subject}</span>
                  </div>
                )}
                {selectedLog.ticket_topic && (
                  <div>
                    <span className="aa-drawer-meta-label">{t('aiAutomation.ticketTopic', 'Sujet')}</span>
                    <span>{selectedLog.ticket_topic}</span>
                  </div>
                )}
                {selectedLog.assistant_name && (
                  <div>
                    <span className="aa-drawer-meta-label">{t('aiAutomation.aiAgent', 'Agent IA')}</span>
                    <span>{selectedLog.assistant_name}</span>
                  </div>
                )}
                {selectedLog.tracking_number && (
                  <div>
                    <span className="aa-drawer-meta-label">{t('aiAutomation.trackingNumber', 'N° de suivi')}</span>
                    <span>{selectedLog.tracking_number}</span>
                  </div>
                )}
                {selectedLog.ticket_status && (
                  <div>
                    <span className="aa-drawer-meta-label">{t('aiAutomation.ticketStatus', 'Statut du ticket')}</span>
                    <span>{t(`ticketStatuses.${selectedLog.ticket_status}`, selectedLog.ticket_status)}</span>
                  </div>
                )}
                <div>
                  <span className="aa-drawer-meta-label">{t('aiAutomation.date', 'Date')}</span>
                  <span>{formatDate(selectedLog.created_at)}</span>
                </div>
                <div>
                  <span className="aa-drawer-meta-label">{t('aiAutomation.source', 'Source')}</span>
                  <span>{sourceLabel(selectedLog.source)}</span>
                </div>
                <div>
                  <span className="aa-drawer-meta-label">{t('aiAutomation.scope', 'Portée')}</span>
                  <span>{SCOPE_LABELS[selectedLog.scope] || selectedLog.scope || '—'}</span>
                </div>
                {selectedLog.brand_id && (
                  <div>
                    <span className="aa-drawer-meta-label">{t('aiAutomation.brand', 'Marque')}</span>
                    <span>{getBrandName(selectedLog.brand_id)}</span>
                  </div>
                )}
                {typeof selectedLog.processing_time_ms === 'number' && (
                  <div>
                    <span className="aa-drawer-meta-label">{t('aiAutomation.processing', 'Temps')}</span>
                    <span>{selectedLog.processing_time_ms} ms</span>
                  </div>
                )}
              </div>

              {selectedLog.reason && (
                <div className="aa-drawer-section">
                  <h4>{t('aiAutomation.reason', 'Raison')}</h4>
                  <div className="aa-drawer-text">{selectedLog.reason}</div>
                </div>
              )}
              {selectedLog.customer_message_snippet && (
                <div className="aa-drawer-section">
                  <h4>{t('aiAutomation.customerMsg', 'Message client')}</h4>
                  <div className="aa-drawer-text">{selectedLog.customer_message_snippet}</div>
                </div>
              )}
              {selectedLog.ai_response_snippet && (
                <div className="aa-drawer-section">
                  <h4>{t('aiAutomation.aiResponse', 'Réponse de l’IA')}</h4>
                  <div className="aa-drawer-text">{selectedLog.ai_response_snippet}</div>
                </div>
              )}

              {selectedLog.ticket_id && (
                <button
                  type="button"
                  className="u-btn u-btn--primary aa-drawer-ticket"
                  onClick={() => navigate(`/tickets/${selectedLog.ticket_id}/messages`)}
                >
                  {t('aiAutomation.goToTicket', 'Ouvrir le ticket')} <FaExternalLinkAlt />
                </button>
              )}

              <button type="button" className="aa-drawer-ids-toggle" onClick={() => setShowLogIds((v) => !v)}>
                {showLogIds ? <FaChevronDown /> : <FaChevronRight />}
                {t('aiAutomation.technicalIds', 'Identifiants techniques')}
              </button>
              {showLogIds && (
                <div className="aa-drawer-ids">
                  <div><span>{t('aiAutomation.ticketId', 'Ticket')}</span><code>{selectedLog.ticket_id || '—'}</code></div>
                  {selectedLog.assistant_id && (
                    <div><span>{t('aiAutomation.aiAgent', 'Agent IA')}</span><code>{selectedLog.assistant_id}</code></div>
                  )}
                  {selectedLog.message_id && (
                    <div><span>{t('aiAutomation.messageId', 'Message')}</span><code>{selectedLog.message_id}</code></div>
                  )}
                  <div><span>{t('aiAutomation.logId', 'Log')}</span><code>{selectedLog.id}</code></div>
                </div>
              )}
            </div>
          </aside>
        </>
      )}

      {/* Rule modal */}
      {ruleModal && (
        <div className="aa-modal-backdrop" onClick={() => setRuleModal(null)}>
          <div className="aa-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="aa-modal-header">
              <h3>
                {ruleModal.editing
                  ? t('aiAutomation.editRule', 'Modifier la règle')
                  : t('aiAutomation.createRule', 'Créer une règle')}
              </h3>
              <button type="button" className="aa-icon-btn" onClick={() => setRuleModal(null)} aria-label={t('common.cancel', 'Annuler')}>
                <FaTimes />
              </button>
            </div>
            <div className="aa-modal-body">
              <div className="aa-form-section">
                <h4>{t('aiAutomation.sectionScope', 'Portée')}</h4>
                <div className="aa-form-grid">
                  {ruleModal.editing ? (
                    <div className="aa-field">
                      <label>{t('aiAutomation.brand', 'Marque')}</label>
                      <p className="aa-field-locked">{getBrandName(ruleModal.editing.brand_id)}</p>
                    </div>
                  ) : (
                    <div className="aa-field">
                      <label htmlFor="aa-brand">{t('aiAutomation.brand', 'Marque')}</label>
                      <select
                        id="aa-brand"
                        value={ruleModal.form.brand_id}
                        onChange={(e) => setRuleForm({ brand_id: e.target.value })}
                      >
                        <option value="">{t('aiAutomation.allBrands', 'Toutes les marques')}</option>
                        {brands.map((b) => <option key={brandId(b)} value={brandId(b)}>{b.name}</option>)}
                      </select>
                    </div>
                  )}
                  <div className="aa-field">
                    <label htmlFor="aa-scope">{t('aiAutomation.scope', 'Portée')}</label>
                    <select
                      id="aa-scope"
                      value={ruleModal.form.scope}
                      onChange={(e) => setRuleForm({ scope: e.target.value })}
                    >
                      <option value="all">{SCOPE_LABELS.all}</option>
                      <option value="client">{SCOPE_LABELS.client}</option>
                      <option value="carrier">{SCOPE_LABELS.carrier}</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="aa-form-section">
                <h4>{t('aiAutomation.sectionInstructions', 'Instructions')}</h4>
                <p className="aa-field-hint">
                  {t('aiAutomation.instructionsHint', 'Critères que l’IA applique pour juger si sa propre réponse peut partir sans validation humaine.')}
                </p>
                <textarea
                  rows={6}
                  maxLength={100000}
                  value={ruleModal.form.instructions}
                  onChange={(e) => setRuleForm({ instructions: e.target.value })}
                  placeholder={t('aiAutomation.instructionsPlaceholder', 'Ex. : approuver uniquement les réponses de suivi de colis sans engagement financier…')}
                />
              </div>

              <div className="aa-form-section">
                <h4>{t('aiAutomation.sectionDecision', 'Décision')}</h4>
                <div className="aa-field">
                  <label htmlFor="aa-threshold">
                    {t('aiAutomation.threshold', 'Seuil de confiance')}
                    <b> {Math.round(ruleModal.form.confidence_threshold * 100)} %</b>
                  </label>
                  <input
                    id="aa-threshold"
                    type="range"
                    min="50"
                    max="100"
                    step="5"
                    value={Math.round(ruleModal.form.confidence_threshold * 100)}
                    onChange={(e) => setRuleForm({ confidence_threshold: Number(e.target.value) / 100 })}
                  />
                  <p className="aa-field-hint">
                    {t('aiAutomation.thresholdHint', 'En dessous du seuil, la réponse n’est pas envoyée : le repli s’applique.')}
                  </p>
                </div>
                <div className="aa-form-grid">
                  <div className="aa-field">
                    <label htmlFor="aa-fallback">{t('aiAutomation.fallbackAction', 'Repli')}</label>
                    <select
                      id="aa-fallback"
                      value={ruleModal.form.fallback_action}
                      onChange={(e) => setRuleForm({ fallback_action: e.target.value })}
                    >
                      <option value="draft">{t('aiAutomation.fallbackDraft', 'Brouillon à valider')}</option>
                      <option value="assign_employee">{t('aiAutomation.fallbackAssignOption', 'Assigner à un employé')}</option>
                    </select>
                  </div>
                  {ruleModal.form.fallback_action === 'assign_employee' && (
                    <div className="aa-field">
                      <label htmlFor="aa-employee">{t('aiAutomation.fallbackEmployee', 'Employé')}</label>
                      <select
                        id="aa-employee"
                        value={ruleModal.form.fallback_employee_id}
                        onChange={(e) => setRuleForm({ fallback_employee_id: e.target.value })}
                      >
                        <option value="">{t('aiAutomation.selectEmployee', 'Sélectionner…')}</option>
                        {employees.map((emp) => (
                          <option key={emp.id || emp._id} value={emp.id || emp._id}>{emp.name || emp.email}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>

              <div className="aa-form-section">
                <h4>{t('aiAutomation.sectionActivation', 'Activation')}</h4>
                <div className="aa-activation-row">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={ruleModal.form.is_active}
                    className={`aa-switch large ${ruleModal.form.is_active ? 'on' : ''}`}
                    onClick={() => setRuleForm({ is_active: !ruleModal.form.is_active })}
                  >
                    <span className="aa-switch-knob" />
                  </button>
                  <span className="aa-activation-label">
                    {ruleModal.form.is_active
                      ? t('aiAutomation.activeLabel', 'Active — la règle s’applique dès l’enregistrement')
                      : t('aiAutomation.inactiveLabel', 'Inactive — aucune réponse automatique pour cette portée')}
                  </span>
                </div>
              </div>

              {ruleModal.error && <div className="aa-modal-error">{ruleModal.error}</div>}
            </div>
            <div className="aa-modal-footer">
              <button type="button" className="u-btn u-btn--secondary" onClick={() => setRuleModal(null)} disabled={ruleModal.saving}>
                {t('common.cancel', 'Annuler')}
              </button>
              <button type="button" className="u-btn u-btn--primary" onClick={saveRule} disabled={ruleModal.saving}>
                {ruleModal.saving
                  ? (<><FaSpinner className="aa-spin" /> {t('common.saving', 'Enregistrement…')}</>)
                  : t('common.save', 'Enregistrer')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {deleteModal && (
        <div className="aa-modal-backdrop" onClick={() => setDeleteModal(null)}>
          <div className="aa-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="aa-modal-header">
              <h3>{t('aiAutomation.deleteTitle', 'Supprimer la règle')}</h3>
              <button type="button" className="aa-icon-btn" onClick={() => setDeleteModal(null)} aria-label={t('common.cancel', 'Annuler')}>
                <FaTimes />
              </button>
            </div>
            <div className="aa-modal-body">
              <p className="aa-delete-text">
                {t('aiAutomation.deleteConfirm', 'Supprimer la règle « {{scope}} » de {{brand}} ? Les réponses IA de cette portée ne seront plus envoyées automatiquement (sauf règle globale ou bascule héritée).', {
                  scope: SCOPE_LABELS[deleteModal.rule.scope] || deleteModal.rule.scope,
                  brand: getBrandName(deleteModal.rule.brand_id),
                })}
              </p>
              {deleteModal.error && <div className="aa-modal-error">{deleteModal.error}</div>}
            </div>
            <div className="aa-modal-footer">
              <button type="button" className="u-btn u-btn--secondary" onClick={() => setDeleteModal(null)} disabled={deleteModal.deleting}>
                {t('common.cancel', 'Annuler')}
              </button>
              <button type="button" className="aa-danger-btn" onClick={confirmDeleteRule} disabled={deleteModal.deleting}>
                {deleteModal.deleting
                  ? (<><FaSpinner className="aa-spin" /> {t('aiAutomation.deleting', 'Suppression…')}</>)
                  : (<><FaTrash /> {t('aiAutomation.delete', 'Supprimer')}</>)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIAutomation;
